import {
  Code,
  DeleteOutline,
  ContentCopyOutlined,
  EditOutlined,
  FileDownloadOutlined,
  UploadFile,
} from "@mui/icons-material";
import { Box, FormLabel, IconButton, IconButtonProps, Input, Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { AppFeatureLookup, defaultUnauthorizedMessage } from "../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage, getAllowedCampaignActions } from "../../App/Constants/CrmLiteConstants";
import { parseOverwriteFormConfig } from "../../CreateCampaign/Helpers/CampaignFunctions";
import { useExportFormConfig } from "../../App/Queries/CampaignQueries";
import { useFeaturePermission } from "../../App/State/Hooks/abacHooks";
import { useIsSuperUser } from "../../App/State/Hooks/useIsSuperUser";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { ICampaign } from "../../App/Typings/Campaigns";
import { CampaignQuickActions, CampaignTypes } from "../../App/Enums/CampaignTypes";
import { getCampaignTypeDisplay } from "./CampaignDetails";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { ChangeEventHandler, useState } from "react";
import CampaignNotificationsModal from "./CampaignNotificationsModal";

interface CampaignButtonsProps {
  campaign: ICampaign;
  handleOpenEmbedScriptDialog: (campaign: ICampaign) => void;
  handleOpenDeleteDialog: (campaign: ICampaign) => void;
}

export default function CampaignButtons({
  campaign,
  handleOpenEmbedScriptDialog,
  handleOpenDeleteDialog,
}: CampaignButtonsProps) {
  const campaignActions = getAllowedCampaignActions(campaign.campaignType);
  const disableEditButton = !campaignActions.includes(CampaignQuickActions.Edit);
  const disableDuplicateButton = !campaignActions.includes(CampaignQuickActions.Duplicate);
  const disableEmbedButton = !campaignActions.includes(CampaignQuickActions.EmbedScript);
  const disableDeleteButton = !campaignActions.includes(CampaignQuickActions.Delete);
  const disableExportButton = !campaignActions.includes(CampaignQuickActions.Export);
  const disableOverwriteButton = !campaignActions.includes(CampaignQuickActions.Overwrite);
  const [campaignModalOpen, setCampaignModalOpen] = useState<boolean>(false);

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "row wrap" }}>
        <EditCampaignButton campaign={campaign} disabledByType={disableEditButton} />
        <DuplicateCampaignButton campaign={campaign} disabledByType={disableDuplicateButton} />
        <ExportCampaignButton campaign={campaign} disabledByType={disableExportButton} />
        <OverwriteCampaignButton campaign={campaign} disabledByType={disableOverwriteButton} />
        <EmbedScriptButton
          campaignType={campaign.campaignType}
          onClick={() => handleOpenEmbedScriptDialog(campaign)}
          disabledByType={disableEmbedButton}
        />
        <DeleteCampaignButton
          campaignType={campaign.campaignType}
          onClick={() => handleOpenDeleteDialog(campaign)}
          disabledByType={disableDeleteButton}
        />
        <CampaignNotificationsButton onClick={() => setCampaignModalOpen(true)} />
      </Box>
      <CampaignNotificationsModal
        campaign={campaign}
        isOpen={campaignModalOpen}
        handleModalClose={() => setCampaignModalOpen(false)}
      />
    </>
  );
}

const EditCampaignButton = ({
  campaign,
  disabledByType,
}: {
  campaign: ICampaign;
  disabledByType?: boolean;
}) => {
  const accessEditCampaign = useFeaturePermission(AppFeatureLookup.CampaignEditButton);
  const title = accessEditCampaign
    ? disabledByType
      ? `Cannot Edit ${getCampaignTypeDisplay(campaign.campaignType)} Forms`
      : "Edit Form"
    : defaultUnauthorizedMessage;

  const buttonProps = {
    "aria-label": title,
    "data-test": "link-edit-campaign",
    sx: { width: "auto" },
  } as IconButtonProps & { component?: any; to?: string };

  if (disabledByType) {
    buttonProps.disabled = true;
  } else if (accessEditCampaign) {
    buttonProps.component = Link;
    buttonProps.to = `/leadcapture/forms/edit/${campaign.id}/${campaign.forms[0]?.id}`;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>
          <EditOutlined />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const DuplicateCampaignButton = ({
  campaign,
  disabledByType,
}: {
  campaign: ICampaign;
  disabledByType?: boolean;
}) => {
  const accessDuplicateCampaign = useFeaturePermission(AppFeatureLookup.CampaignDuplicateButton);
  const title = accessDuplicateCampaign
    ? disabledByType
      ? `Cannot Duplicate ${getCampaignTypeDisplay(campaign.campaignType)} Forms`
      : "Duplicate Form"
    : crmLiteUpgradeMessage;

  const buttonProps = {
    "aria-label": title,
    "data-test": "link-duplicate-campaign",
    sx: { width: "auto" },
  } as IconButtonProps & { component?: any; to?: string };

  if (disabledByType) {
    buttonProps.disabled = true;
  } else if (accessDuplicateCampaign) {
    buttonProps.component = Link;
    buttonProps.to = `/leadcapture/forms/duplicate/${campaign.id}/${campaign.forms[0]?.id}`;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>
          <ContentCopyOutlined />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const ExportCampaignButton = ({
  campaign,
  disabledByType,
}: {
  campaign: ICampaign;
  disabledByType?: boolean;
}) => {
  const isSuperUser = useIsSuperUser();
  const { displayError } = useNotification();
  const { mutate: exportFormConfig } = useExportFormConfig();
  const hasForm = !!campaign.forms?.[0]?.id;
  const isDisabled = disabledByType || !hasForm;
  const title = disabledByType
    ? `Cannot Export ${getCampaignTypeDisplay(campaign.campaignType)} Forms`
    : !hasForm
      ? "No form configuration to export"
      : "Export Form Configuration";

  const buttonProps = {
    "aria-label": title,
    "data-test": "button-export-campaign",
    sx: { width: "auto" },
  } as IconButtonProps;

  if (isDisabled) {
    buttonProps.disabled = true;
  } else {
    buttonProps.onClick = () =>
      exportFormConfig(
        {
          campaignId: campaign.id,
          formId: campaign.forms[0].id,
          campaignName: campaign.name,
        },
        { onError: () => displayError("There was an error exporting the form configuration.") },
      );
  }

  // Export is a super-user-only feature, hidden entirely (not just disabled) for everyone else
  if (!isSuperUser) {
    return null;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>
          <FileDownloadOutlined />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const OverwriteCampaignButton = ({
  campaign,
  disabledByType,
}: {
  campaign: ICampaign;
  disabledByType?: boolean;
}) => {
  const isSuperUser = useIsSuperUser();
  const { displayError } = useNotification();
  const navigate = useNavigate();
  const hasForm = !!campaign.forms?.[0]?.id;
  const isDisabled = disabledByType || !hasForm;
  const title = disabledByType
    ? `Cannot Overwrite ${getCampaignTypeDisplay(campaign.campaignType)} Forms`
    : !hasForm
      ? "No form configuration to overwrite"
      : "Import — Overwrite This Form";

  const handleUploadFile: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      displayError("That file isn't a valid form configuration.");
      return;
    }

    // Guards `campaign.forms[0].id` directly rather than trusting the render-time `hasForm`
    // check alone — this handler can still fire via a forced/synthetic change event that bypasses
    // the disabled input, so the read needs its own null-safety regardless of `isDisabled`
    const formId = campaign.forms?.[0]?.id;
    if (!formId) {
      displayError("This form no longer has a configuration to overwrite.");
      return;
    }

    try {
      const overwriteFormState = parseOverwriteFormConfig(parsed, campaign.campaignType);
      navigate(`/leadcapture/forms/edit/${campaign.id}/${formId}`, {
        state: { overwriteFormState },
      });
    } catch (err) {
      displayError(err instanceof Error ? err.message : "That file isn't a valid form configuration.");
    }
  };

  // Overwrite is a super-user-only feature, hidden entirely (not just disabled) for everyone else
  if (!isSuperUser) {
    return null;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <FormLabel htmlFor="overwrite-campaign-config-input" sx={{ width: "auto" }}>
          <Input
            id="overwrite-campaign-config-input"
            type="file"
            inputProps={{
              accept: "application/json,.json",
              "data-test": "input-overwrite-campaign-config",
            }}
            style={{ display: "none" }}
            disabled={isDisabled}
            onChange={handleUploadFile}
          />
          <IconButton
            component="span"
            aria-label={title}
            data-test="button-overwrite-campaign"
            disabled={isDisabled}
            sx={{ width: "auto" }}
          >
            <UploadFile />
          </IconButton>
        </FormLabel>
      </Box>
    </Tooltip>
  );
};

const EmbedScriptButton = ({
  campaignType,
  onClick,
  disabledByType,
}: {
  campaignType: CampaignTypes;
  onClick: () => void;
  disabledByType?: boolean;
}) => {
  const accessCampaignEmbedCode = useFeaturePermission(AppFeatureLookup.CampaignGetEmbedCodeButton);
  const title = accessCampaignEmbedCode
    ? disabledByType
      ? `Cannot Get Embed Code for ${getCampaignTypeDisplay(campaignType)} Forms`
      : "Get Embed Code"
    : defaultUnauthorizedMessage;

  const buttonProps = {
    "aria-label": title,
    "data-test": "toggle-campaign-embedscript-dialog",
    "aria-controls": "campaigns-dialog",
    "aria-haspopup": "true",
    sx: { width: "auto" },
  } as IconButtonProps;

  if (disabledByType) {
    buttonProps.disabled = true;
  } else if (accessCampaignEmbedCode) {
    buttonProps.onClick = onClick;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>
          <Code />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const DeleteCampaignButton = ({
  campaignType,
  onClick,
  disabledByType,
}: {
  campaignType: CampaignTypes;
  onClick: () => void;
  disabledByType?: boolean;
}) => {
  const accessDeleteCampaign = useFeaturePermission(AppFeatureLookup.CampaignDeleteButton);
  const title = accessDeleteCampaign
    ? disabledByType
      ? `Cannot Delete ${getCampaignTypeDisplay(campaignType)} Forms`
      : "Delete Form"
    : crmLiteUpgradeMessage;

  const buttonProps = {
    "aria-label": title,
    "data-test": "toggle-delete-campaign-dialog",
    "aria-controls": "campaigns-dialog",
    "aria-haspopup": "true",
    sx: { width: "auto" },
  } as IconButtonProps;

  if (disabledByType) {
    buttonProps.disabled = true;
  } else if (accessDeleteCampaign) {
    buttonProps.onClick = onClick;
  }

  return (
    <Tooltip title={title} arrow>
      <Box>
        <IconButton {...buttonProps}>
          <DeleteOutline />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

const CampaignNotificationsButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Tooltip title={"Notifications"} arrow>
      <Box>
        <IconButton onClick={onClick}>
          <NotificationsNoneOutlinedIcon />
        </IconButton>
      </Box>
    </Tooltip>
  );
};
